import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const reportTypes = [
  { id: 'gap',        label: 'Gap Analysis',            description: 'Keyword opportunities in positions 5–20' },
  { id: 'competitor', label: 'Competitor Intelligence',  description: 'Why competitors outrank you and how to beat them' },
  { id: 'ai',         label: 'AI Visibility Audit',     description: 'Citation presence in Google AI Overview and GEO signals' },
  { id: 'content',    label: 'Content Plan',             description: '4 prioritised article briefs based on gap data' },
  { id: 'full',       label: 'Full Report',              description: 'All of the above in one complete analysis' },
];

const dateRanges = [
  { id: '30', label: 'Last 30 days' },
  { id: '60', label: 'Last 60 days' },
  { id: '90', label: 'Last 90 days' },
];

export default function RunReport() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientId, setClientId] = useState('');
  const [selected, setSelected] = useState({ gap: true, competitor: false, ai: false, content: false, full: false });
  const [dateRange, setDateRange] = useState('90');
  const [running, setRunning] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [progressPct, setProgressPct] = useState(0);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!session?.user?.id) return;
    let cancelled = false;
    async function fetchClients() {
      setClientsLoading(true);
      const { data, error } = await supabase
        .from('clients')
        .select('id, business_name, domain')
        .eq('user_id', session.user.id)
        .order('business_name', { ascending: true });
      if (cancelled) return;
      if (error) console.error('Failed to load clients:', error);
      else setClients(data || []);
      setClientsLoading(false);
    }
    fetchClients();
    return () => { cancelled = true; };
  }, [session]);

  function toggleType(id) {
    if (id === 'full') {
      const allOn = !selected.full;
      setSelected({ gap: allOn, competitor: allOn, ai: allOn, content: allOn, full: allOn });
    } else {
      const next = { ...selected, [id]: !selected[id] };
      const allChecked = next.gap && next.competitor && next.ai && next.content;
      setSelected({ ...next, full: allChecked });
    }
  }

  function anySelected() {
    return selected.gap || selected.competitor || selected.ai || selected.content || selected.full;
  }

  const needsAI = selected.ai || selected.full;

  async function handleRun() {
    if (!clientId) { setError('Please select a client.'); return; }
    if (!anySelected()) { setError('Please select at least one report type.'); return; }
    setError('');

    const client = clients.find(c => c.id === clientId);
    const reportTypeLabel = selected.full
      ? 'Full Report'
      : reportTypes.filter(t => t.id !== 'full' && selected[t.id]).map(t => t.label).join(', ');

    // Step 1 — create the Processing record
    setProgressMsg('Creating report record…');
    setProgressPct(5);
    setRunning(true);
    setDone(false);

    const { data: insertData, error: insertError } = await supabase
      .from('reports')
      .insert({
        client_id:       clientId,
        user_id:         session.user.id,
        report_type:     reportTypeLabel,
        status:          'Processing',
        date_range_days: Number(dateRange),
      })
      .select('id')
      .single();

    if (insertError) {
      console.error('Failed to create report:', insertError);
      setError(insertError.message || 'Failed to start report. Please try again.');
      setRunning(false);
      return;
    }

    const newReportId = insertData.id;
    let gapData = null;
    let aiData  = null;

    // Step 2 — gap analysis
    setProgressMsg('Connecting to DataForSEO…');
    setProgressPct(15);

    try {
      setProgressMsg('Pulling keyword rankings…');
      setProgressPct(30);

      const gapRes = await fetch('/.netlify/functions/run-gap-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain:         client.domain,
          dateRangeDays:  Number(dateRange),
          clientId,
          userId:         session.user.id,
        }),
      });

      if (gapRes.ok) {
        const gapJson = await gapRes.json();
        gapData = gapJson.data ?? null;
        setProgressPct(55);
      } else {
        console.error('Gap analysis API error:', gapRes.status);
      }
    } catch (err) {
      console.error('Gap analysis fetch failed:', err);
    }

    // Step 3 — AI visibility (only if requested)
    if (needsAI) {
      setProgressMsg('Running AI visibility audit…');
      setProgressPct(65);

      try {
        const topKeywords = gapData?.raw_keywords
          ? gapData.raw_keywords.slice(0, 5).map(k => k.keyword)
          : [];

        if (topKeywords.length > 0) {
          const aiRes = await fetch('/.netlify/functions/run-ai-visibility', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ domain: client.domain, keywords: topKeywords }),
          });

          if (aiRes.ok) {
            const aiJson = await aiRes.json();
            aiData = aiJson.data ?? null;
          } else {
            console.error('AI visibility API error:', aiRes.status);
          }
        } else {
          console.warn('No keywords from gap analysis to pass to AI visibility.');
        }
      } catch (err) {
        console.error('AI visibility fetch failed:', err);
      }
    }

    // Step 4 — save results and mark Ready
    setProgressMsg('Saving report…');
    setProgressPct(90);

    const updatePayload = { status: 'Ready' };
    if (gapData)  updatePayload.gap_analysis  = gapData;
    if (aiData)   updatePayload.ai_visibility  = aiData;

    const { error: updateError } = await supabase
      .from('reports')
      .update(updatePayload)
      .eq('id', newReportId);

    if (updateError) {
      console.error('Failed to save report data:', updateError);
    }

    setProgressMsg('Report complete!');
    setProgressPct(100);
    setDone(true);
  }

  function handleReset() {
    setRunning(false);
    setDone(false);
    setProgressMsg('');
    setProgressPct(0);
  }

  const selectedClient = clients.find(c => c.id === clientId);

  return (
    <div style={{ maxWidth: '680px' }}>

      {/* Header */}
      <div style={{ marginBottom: '2.25rem' }}>
        <h1 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: '2.2rem',
          fontWeight: 600,
          color: 'var(--charcoal)',
          lineHeight: 1.15,
          marginBottom: '0.3rem',
        }}>
          Run Report
        </h1>
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.9rem',
          color: 'var(--charcoal-muted)',
        }}>
          Generate a fresh SEO &amp; GEO analysis for any client.
        </p>
      </div>

      {/* Form card */}
      <div style={{
        background: 'var(--white)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden',
        marginBottom: '1.25rem',
      }}>

        {/* Client selector */}
        <Section label="Client">
          <select
            value={clientId}
            onChange={e => { setClientId(e.target.value); setError(''); }}
            disabled={running}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.9rem',
              color: clientId ? 'var(--charcoal)' : 'var(--charcoal-muted)',
              background: 'var(--white)',
              border: '1.5px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.6rem 0.85rem',
              width: '100%',
              outline: 'none',
              cursor: running ? 'not-allowed' : 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%237A7A7A' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.85rem center',
              paddingRight: '2.25rem',
            }}
          >
            <option value="">
              {clientsLoading ? 'Loading clients…' : clients.length === 0 ? 'No clients yet — add one first' : 'Select a client…'}
            </option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.business_name} — {c.domain}</option>
            ))}
          </select>
        </Section>

        <Divider />

        {/* Report type */}
        <Section label="Report Type">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {reportTypes.map(type => (
              <label
                key={type.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  cursor: running ? 'not-allowed' : 'pointer',
                  padding: '0.75rem 0.9rem',
                  borderRadius: 'var(--radius-sm)',
                  border: `1.5px solid ${selected[type.id] ? 'var(--blush-dark)' : 'var(--border)'}`,
                  background: selected[type.id] ? 'var(--blush-light)' : 'var(--white)',
                  transition: 'all 0.15s ease',
                  opacity: running ? 0.6 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={selected[type.id]}
                  onChange={() => !running && toggleType(type.id)}
                  style={{
                    marginTop: '2px',
                    accentColor: 'var(--blush-dark)',
                    width: '15px',
                    height: '15px',
                    flexShrink: 0,
                    cursor: running ? 'not-allowed' : 'pointer',
                  }}
                />
                <div>
                  <div style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    fontWeight: selected[type.id] ? 500 : 400,
                    color: 'var(--charcoal)',
                    marginBottom: '0.1rem',
                  }}>
                    {type.label}
                    {type.id === 'full' && (
                      <span style={{
                        marginLeft: '0.5rem',
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.68rem',
                        fontWeight: 500,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        background: 'var(--gold-light)',
                        color: '#7A5C1E',
                        padding: '0.1rem 0.5rem',
                        borderRadius: '999px',
                      }}>
                        Recommended
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.78rem',
                    color: 'var(--charcoal-muted)',
                  }}>
                    {type.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </Section>

        <Divider />

        {/* Date range */}
        <Section label="Date Range">
          <div style={{ display: 'flex', gap: '0.65rem' }}>
            {dateRanges.map(r => (
              <button
                key={r.id}
                onClick={() => !running && setDateRange(r.id)}
                disabled={running}
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.85rem',
                  fontWeight: dateRange === r.id ? 500 : 400,
                  color: dateRange === r.id ? 'var(--charcoal)' : 'var(--charcoal-muted)',
                  background: dateRange === r.id ? 'var(--blush-light)' : 'var(--white)',
                  border: `1.5px solid ${dateRange === r.id ? 'var(--blush-dark)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius-sm)',
                  padding: '0.5rem 1.1rem',
                  cursor: running ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s ease',
                  opacity: running ? 0.6 : 1,
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </Section>

        {/* Error */}
        {error && (
          <div style={{ padding: '0 1.75rem 1.25rem' }}>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.8rem',
              color: '#B91C1C',
              background: '#FDECEA',
              padding: '0.6rem 0.85rem',
              borderRadius: 'var(--radius-sm)',
            }}>
              {error}
            </p>
          </div>
        )}

        {/* Run button */}
        <div style={{
          padding: '1.25rem 1.75rem 1.75rem',
          borderTop: running ? '1px solid var(--border)' : 'none',
        }}>
          {!running ? (
            <button
              onClick={handleRun}
              style={{
                width: '100%',
                background: 'var(--charcoal)',
                color: 'var(--white)',
                fontFamily: 'var(--font-heading)',
                fontWeight: 600,
                fontSize: '1.15rem',
                letterSpacing: '0.02em',
                padding: '0.9rem 1.5rem',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--charcoal-light)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--charcoal)'}
            >
              <span>▷</span> Run Report
            </button>
          ) : (
            <ProgressPanel
              message={progressMsg}
              pct={progressPct}
              done={done}
              client={selectedClient}
              onReset={handleReset}
              navigate={navigate}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ProgressPanel({ message, pct, done, client, onReset, navigate }) {
  return (
    <div>
      {/* Client context */}
      {client && (
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.78rem',
          color: 'var(--charcoal-muted)',
          marginBottom: '1.1rem',
          letterSpacing: '0.02em',
        }}>
          Running for <strong style={{ color: 'var(--charcoal)', fontWeight: 500 }}>{client.business_name}</strong> — {client.domain}
        </p>
      )}

      {/* Progress bar */}
      <div style={{
        height: '6px',
        background: 'var(--border)',
        borderRadius: '999px',
        overflow: 'hidden',
        marginBottom: '1rem',
      }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          background: done
            ? 'var(--gold)'
            : 'linear-gradient(90deg, var(--blush-dark), var(--blush))',
          borderRadius: '999px',
          transition: 'width 0.7s ease',
        }} />
      </div>

      {/* Status message */}
      {!done ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.25rem' }}>
          <Spinner />
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.875rem',
            color: 'var(--charcoal-light)',
          }}>
            {message}
          </p>
        </div>
      ) : (
        <div style={{ textAlign: 'center', paddingTop: '0.5rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: '#ECFDF5',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 0.75rem',
            fontSize: '1.3rem',
          }}>
            ✓
          </div>
          <h3 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.4rem',
            fontWeight: 600,
            color: 'var(--charcoal)',
            marginBottom: '0.3rem',
          }}>
            Report Complete!
          </h3>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.85rem',
            color: 'var(--charcoal-muted)',
            marginBottom: '1.5rem',
          }}>
            Your analysis is ready to review.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <button
              onClick={() => navigate('/reports')}
              style={{
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
                fontSize: '0.9rem',
                color: 'var(--white)',
                background: 'var(--gold)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                padding: '0.7rem 1.75rem',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              View Report →
            </button>
            <button
              onClick={onReset}
              style={{
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
                fontSize: '0.9rem',
                color: 'var(--charcoal)',
                background: 'transparent',
                border: '1.5px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.7rem 1.4rem',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'var(--blush-dark)';
                e.currentTarget.style.background = 'var(--blush-light)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Run Another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <div style={{
      width: '16px',
      height: '16px',
      border: '2px solid var(--border)',
      borderTopColor: 'var(--blush-dark)',
      borderRadius: '50%',
      flexShrink: 0,
      animation: 'spin 0.7s linear infinite',
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ padding: '1.4rem 1.75rem' }}>
      <p style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.7rem',
        fontWeight: 500,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: 'var(--charcoal-muted)',
        marginBottom: '0.85rem',
      }}>
        {label}
      </p>
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{ height: '1px', background: 'var(--border)' }} />;
}
